/**
 * OnboardingTour — first-run guided walk-through.
 *
 * Shows once per browser when `localStorage.tourCompleted.v1 !== '1'`.
 * Five steps, each with a Next button; user can Skip at any time.
 * Doesn't try to highlight UI elements (which is fragile across layouts);
 * just walks the user through the recommended order of pages with a clear
 * call-to-action.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  Stepper, Step, StepLabel, MobileStepper,
} from '@mui/material';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

const TOUR_KEY = 'tourCompleted.v1';

interface TourStep {
  title: string;
  body: React.ReactNode;
  ctaLabel?: string;
  ctaPath?: string;
}

const STEPS: TourStep[] = [
  {
    title: 'Welcome to earngush',
    body: (
      <Box>
        <Typography variant="body2" color="text.secondary">
          Quick 5-step tour so you know where things live. Press <kbd>?</kbd> any time for the full
          keyboard cheat sheet, or <kbd>Ctrl/⌘ K</kbd> for the command palette.
        </Typography>
      </Box>
    ),
  },
  {
    title: 'Dashboard — your morning desk',
    body: (
      <Typography variant="body2" color="text.secondary">
        Live positions, Recent Alerts (with one-click Execute), Quick Trade FAB, and the Risk Cockpit
        showing your open risk vs daily-loss cap. Most decisions start here.
      </Typography>
    ),
    ctaLabel: 'Open Dashboard',
    ctaPath: '/dashboard',
  },
  {
    title: 'Strategies — automate signals',
    body: (
      <Typography variant="body2" color="text.secondary">
        RockBand · DailyBlast · Sniper engines scan your watchlists. Set scan cadence + route flags
        and let them fire signals to Dashboard / Telegram.
      </Typography>
    ),
    ctaLabel: 'Open Strategies',
    ctaPath: '/strategies',
  },
  {
    title: 'Analytics — learn from past trades',
    body: (
      <Typography variant="body2" color="text.secondary">
        Win rate, R-multiple distribution, drawdown, per-strategy breakdown. Tag your trades to
        slice analytics by setup (FOMC, breakout, fakeout, …).
      </Typography>
    ),
    ctaLabel: 'Open Analytics',
    ctaPath: '/analytics',
  },
  {
    title: 'Settings — connect & configure',
    body: (
      <Typography variant="body2" color="text.secondary">
        Broker keys (with Test Connection), Telegram, the daily-loss circuit breaker, and your
        password. Everything is one place.
      </Typography>
    ),
    ctaLabel: 'Open Settings',
    ctaPath: '/settings',
  },
];

const OnboardingTour: React.FC = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    try {
      if (localStorage.getItem(TOUR_KEY) === '1') return;
    } catch { /* ignore */ }
    // Defer a tick so the rest of the layout renders first
    const t = setTimeout(() => setOpen(true), 800);
    return () => clearTimeout(t);
  }, []);

  const finish = () => {
    try { localStorage.setItem(TOUR_KEY, '1'); } catch { /* ignore */ }
    setOpen(false);
  };

  const handleNext = () => {
    if (step < STEPS.length - 1) {
      setStep(s => s + 1);
    } else {
      finish();
    }
  };

  const handleBack = () => setStep(s => Math.max(0, s - 1));

  const handleCta = () => {
    const s = STEPS[step];
    if (s.ctaPath) navigate(s.ctaPath);
    handleNext();
  };

  if (!open) return null;
  const cur = STEPS[step];

  return (
    <Dialog open={open} onClose={finish} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <RocketLaunchIcon color="primary" />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>{cur.title}</Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={step} alternativeLabel sx={{ display: { xs: 'none', sm: 'flex' }, mb: 2 }}>
          {STEPS.map((s, i) => (
            <Step key={i}><StepLabel sx={{ '& .MuiStepLabel-label': { fontSize: '0.65rem' } }}>{s.title.split(' — ')[0]}</StepLabel></Step>
          ))}
        </Stepper>
        <Box sx={{ minHeight: 80 }}>
          {cur.body}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
        <Button onClick={finish} color="inherit" size="small">Skip tour</Button>
        <Box sx={{ flex: 1 }} />
        {cur.ctaLabel && (
          <Button variant="outlined" onClick={handleCta} size="small">{cur.ctaLabel}</Button>
        )}
        <MobileStepper
          variant="dots"
          steps={STEPS.length}
          position="static"
          activeStep={step}
          sx={{ flexGrow: 0, bgcolor: 'transparent', p: 0 }}
          nextButton={
            <Button size="small" onClick={handleNext} disabled={false}>
              {step === STEPS.length - 1 ? 'Done' : 'Next'}
              <KeyboardArrowRightIcon fontSize="small" />
            </Button>
          }
          backButton={
            <Button size="small" onClick={handleBack} disabled={step === 0}>
              <KeyboardArrowLeftIcon fontSize="small" />
              Back
            </Button>
          }
        />
      </DialogActions>
    </Dialog>
  );
};

export default OnboardingTour;
